import { useState, useEffect, useCallback } from 'react';
import { Text, Button, Badge, Spinner, Card, CardHeader } from '@fluentui/react-components';
import { ArrowLeftRegular, ArrowSyncRegular, MoneyRegular } from '@fluentui/react-icons';
import { generateEstimate, getCachedEstimate, type EngagementCostEstimate, type CostCategory } from '../services/cost-service';
import { useApiToken } from '../hooks/useApiToken';
import type { Project } from '../services/project-service';

interface CostEstimatePageProps {
  project: Project;
  onBack: () => void;
}

export function CostEstimatePage({ project, onBack }: CostEstimatePageProps) {
  const [estimate, setEstimate] = useState<EngagementCostEstimate | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const getToken = useApiToken();

  const loadCached = useCallback(async () => {
    if (!project.id) return;
    setLoading(true);
    try {
      const token = await getToken();
      if (!token) return;
      const cached = await getCachedEstimate(project.id, project.customerId, token);
      setEstimate(cached);
    } catch {
      // No cached estimate yet
    } finally {
      setLoading(false);
    }
  }, [project.id, project.customerId, getToken]);

  useEffect(() => { loadCached(); }, [loadCached]);

  const handleGenerate = useCallback(async () => {
    if (!project.id) return;
    setGenerating(true);
    try {
      const token = await getToken();
      if (!token) return;
      const result = await generateEstimate(
        project.id, project.customerId, token,
        project.azureRegion ?? 'southafricanorth', 'USD', '1yr'
      );
      setEstimate(result);
    } catch (err) {
      console.error('Failed to generate estimate:', err);
    } finally {
      setGenerating(false);
    }
  }, [project.id, project.customerId, project.azureRegion, getToken]);

  const fmt = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="page-root page-root--scrollable">
      <div className="page-title-bar">
        <Button appearance="subtle" icon={<ArrowLeftRegular />} onClick={onBack} title="Back to project" />
        <MoneyRegular className="icon-title" />
        <Text size={500} weight="semibold">Cost Estimate — {project.name}</Text>
        <div className="title-bar-actions">
          <Button
            appearance="primary"
            icon={<ArrowSyncRegular />}
            onClick={handleGenerate}
            disabled={generating}
            size="small"
          >
            {generating ? 'Generating...' : estimate ? 'Refresh Estimate' : 'Generate Estimate'}
          </Button>
        </div>
      </div>

      {loading && <Spinner label="Loading..." size="small" style={{ padding: 24 }} />}

      {!loading && !estimate && (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <Text style={{ color: '#616161' }}>
            No cost estimate yet. Add workload register entries, then click "Generate Estimate" to fetch live Azure pricing.
          </Text>
        </div>
      )}

      {estimate && (
        <div style={{ padding: '0 24px 24px' }}>
          {/* Summary cards */}
          <div className="dashboard-grid" style={{ marginBottom: 24, padding: 0 }}>
            <SummaryCard label="Azure Consumption" value={fmt(estimate.azureConsumptionMonthly)} sublabel="per month" colour="brand" />
            <SummaryCard label="W365 Licensing" value={fmt(estimate.w365LicensingMonthly)} sublabel="per month" colour="informative" />
            <SummaryCard label="Grand Total" value={fmt(estimate.grandTotalMonthly)} sublabel="per month" colour="success" />
            <SummaryCard label="Savings Plan" value={estimate.savingsPlanPhase.toUpperCase()} sublabel="pricing tier" colour="warning" />
          </div>

          {/* Category breakdowns */}
          {estimate.categories.map(cat => (
            <CategoryTable key={cat.name} category={cat} fmt={fmt} />
          ))}

          {/* Notes */}
          {estimate.notes.length > 0 && (
            <div style={{ marginTop: 16, fontSize: 12, color: '#616161' }}>
              {estimate.notes.map((n, i) => <div key={i}>* {n}</div>)}
            </div>
          )}

          <div style={{ marginTop: 8, fontSize: 11, color: '#999' }}>
            Generated: {new Date(estimate.generatedAt).toLocaleString()}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, sublabel, colour }: {
  label: string; value: string; sublabel: string; colour: 'brand' | 'success' | 'warning' | 'informative';
}) {
  return (
    <Card size="small">
      <CardHeader
        header={<Text size={200} style={{ color: '#616161' }}>{label}</Text>}
        description={<Text size={200} style={{ color: '#999' }}>{sublabel}</Text>}
      />
      <div style={{ padding: '0 12px 12px' }}>
        <Badge color={colour} size="large" appearance="filled">{value}</Badge>
      </div>
    </Card>
  );
}

function CategoryTable({ category, fmt }: { category: CostCategory; fmt: (n: number) => string }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Text size={400} weight="semibold">{category.name}</Text>
        <Badge appearance="outline" size="small">{fmt(category.subtotalMonthly)}/mo</Badge>
      </div>
      <table className="data-table" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th>Resource</th>
            <th>SKU</th>
            <th>Qty</th>
            <th>Unit Price</th>
            <th>Monthly</th>
            <th>Pricing</th>
          </tr>
        </thead>
        <tbody>
          {category.lineItems.map((item, i) => (
            <tr key={i}>
              <td>{item.name}</td>
              <td style={{ fontSize: 12 }}>{item.sku}</td>
              <td>{item.quantity}</td>
              <td>{fmt(item.unitPriceMonthly)}</td>
              <td><strong>{fmt(item.totalMonthly)}</strong></td>
              <td><Badge size="small" appearance="outline">{item.pricingTier}</Badge></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
